import React, { PureComponent } from 'react';
import { Router, Route } from 'react-router';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import path from 'path';
import PropTypes from 'prop-types';
import querystring from 'querystring';

import BetaRouter from './BetaRouter';
import {
  redirectReferrer,
  releasePath,
  loadRoute,
  errorLoadingRoute
} from './util';

import ENV from '../env';

import {
  embeddedModeEnabled as ldEmbeddedModeEnabled,
  manualReportingEnabled,
  timelineChartEnabled,
  webformsV2Enabled,
  workDoneBetterNavigationEnabled
} from '../selectors/feature';
import { pathname, query } from '../selectors/routing';

import featurePropTypes from '../types/feature';

import context from '../util/context';
import { format } from '../util/url';

const searchPaths = [
  '/build',
  '/data',
  '/pushbots',
  '/search',
  '/start',
  '/tasks'
];

// The below function handles redirecting requests from https://security.pushbot.com/oauth
// to the appropriate experience's oauth redirect handler, maintaining backwards compatibility
// with the legacy experience.

// We are unable to use the session user.experience selector,
// as we are missing the userSession in this context.
export const oauthRedirect = ({ location }) => {
  const qp = querystring.parse(location.search.slice(1));
  let oauthState;
  try {
    oauthState = JSON.parse(Buffer.from(qp.state, 'base64').toString());
    if (oauthState.exp === 'beta') {
      const destination = oauthState.origin + location.search;
      context.location = destination;
      // The below return is for testing purposes.
      return destination;
    }
  } catch (ex) {
    const destination =
      ENV.PUSHBOT_OAUTH_REDIRECT_URL.replace('oauth', 'legacy/oauth') +
      location.search;
    context.location = destination;
    // Same reasoning as above -- the below return is for testing purposes.
    return destination;
  }
};

const referrer = createSelector(pathname, query, (pathname, query) =>
  format({ pathname, query })
);

export const resolvePathname = ({ location }, replace) => {
  const { pathname } = location;
  const resolvedPath = path.resolve(pathname);
  if (pathname !== resolvedPath) {
    return replace({
      ...location,
      pathname: resolvedPath
    });
  }
};

const resolvePathnameOnChange = (prev, next, replace, done) => {
  resolvePathname(next, replace);

  done();
};

const embeddedModeEnabled = () => {
  try {
    const embeddedMode = JSON.parse(ENV.PUSHBOT_EMBEDDED_MODE);
    return embeddedMode;
  } catch (e) {
    return false;
  }
};

const routes = createSelector(
  embeddedModeEnabled,
  manualReportingEnabled,
  timelineChartEnabled,
  webformsV2Enabled,
  workDoneBetterNavigationEnabled,
  (
    embeddedMode,
    manualReportingEnabled,
    timelineChart,
    webformsV2,
    workDoneBetterNavigation
  ) => [
    ...(releasePath
      ? // eslint-disable-next-line react/jsx-key
        [<Route path={releasePath} onEnter={redirectReferrer()} />]
      : []),
    // eslint-disable-next-line react/jsx-key
    <Route onEnter={resolvePathname} onChange={resolvePathnameOnChange}>
      <Route path="oauth" onEnter={oauthRedirect} />
      <Route>
        {BetaRouter({
          embeddedMode,
          manualReportingEnabled,
          timelineChart,
          webformsV2,
          workDoneBetterNavigation
        })}
      </Route>
      <Route
        path="forbidden"
        getComponent={(next, done) => {
          System.import(
            /* webpackChunkName: "FourOThree" */ '../modules/FourOThree'
          )
            .then(loadRoute(done))
            .catch(errorLoadingRoute);
        }}
      />,
      <Route
        path="*"
        getComponent={(next, done) => {
          System.import(
            /* webpackChunkName: "FourOFour" */ '../modules/FourOFour'
          )
            .then(loadRoute(done))
            .catch(errorLoadingRoute);
        }}
      />
    </Route>
  ]
);

@connect((state, props) => ({
  embeddedModeEnabled: ldEmbeddedModeEnabled(state),
  manualReportingEnabled: manualReportingEnabled(state),
  referrer: referrer(state, props),
  routes: routes(state),
  timelineChart: timelineChartEnabled(state),
  webformsV2: webformsV2Enabled(state),
  workDoneBetterNavigation: workDoneBetterNavigationEnabled(state)
}))
export default class VersionRouter extends PureComponent {
  static displayName = 'VersionRouter';

  static propTypes = {
    embeddedModeEnabled: featurePropTypes,
    history: PropTypes.objectOf(PropTypes.func).isRequired,
    manualReportingEnabled: featurePropTypes,
    referrer: PropTypes.string,
    routes: PropTypes.arrayOf(PropTypes.node),
    timelineChart: featurePropTypes,
    webformsV2: featurePropTypes,
    workDoneBetterNavigation: featurePropTypes
  };

  componentDidUpdate(prevProps) {
    const {
      embeddedModeEnabled,
      history,
      manualReportingEnabled,
      referrer,
      timelineChart,
      webformsV2,
      workDoneBetterNavigation
    } = this.props;

    // Force hard refresh when embedded mode feature flag is toggled.
    if (
      prevProps.embeddedModeEnabled === false &&
      embeddedModeEnabled === true
    ) {
      context.location.replace(referrer || '/');
      return;
    }

    switch (false) {
      case timelineChart === prevProps.timelineChart:
      case webformsV2 === prevProps.webformsV2:
      case manualReportingEnabled === prevProps.manualReportingEnabled:
      case workDoneBetterNavigation === prevProps.workDoneBetterNavigation:
        history.push(referrer || '/');
    }
  }

  createElement = (Component, props) => {
    const pathname = props.location.pathname;
    const key = searchPaths.includes(pathname) ? pathname : undefined;
    return <Component key={key} {...props} />;
  };

  render() {
    const { history, routes } = this.props;

    return (
      <Router
        history={history}
        createElement={this.createElement}
        routes={routes}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./js/routes/VersionRouter.js